/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Bearing Index — interchange + dimensions results (IFX.views.search)
 * assets/interchange-views-search.js
 *
 * Owns everything that renders from /api/search: cards, the table density,
 * the results header (count, filters, sort), facet filters (inline chips on
 * desktop, bottom sheet on mobile), compare tray + dialog (/api/compare),
 * copy / save actions, size-group headers for dimensional searches, banners
 * (OEM match, suffix relaxed, hidden brands) and the empty states that
 * point somewhere useful.
 *
 * Contract with the bootstrap (interchange-search.js): init(app) then
 *   runPn(q, opts) · runDims(params) · runFamily(familyKey) · rerender()
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  var IFX = window.IFX;
  if (!IFX || (IFX.views && IFX.views.search)) return;

  var esc = IFX.escapeHtml;
  var DEFAULT_TOL = 0.1;
  var COMPARE_MAX = 4;

  var app = null;
  var last = null;            // { mode, params, data, hits, label, familyKey }
  var facets = {};            // { key: Set<value> }
  var sortKey = "relevance";
  var compare = new Map();    // id → hit
  var refs = {};

  /* ── labels ─────────────────────────────────────────────────────────── */
  var ATTR_LABELS = {
    SEAL_C_2: "Contact seals, both sides", SEAL_C_1: "Contact seal, one side",
    SEAL_NC_2: "Non-contact seals, both sides", SEAL_NC_1: "Non-contact seal, one side",
    SHIELD_2: "Shields, both sides", SHIELD_1: "Shield, one side", OPEN: "Open",
    C2: "C2 clearance", C3: "C3 clearance", C4: "C4 clearance", C5: "C5 clearance", CN: "Normal clearance",
    CAGE_BRASS: "Brass cage", CAGE_POLY: "Polyamide cage", CAGE_STEEL: "Steel cage",
    P4: "P4 precision", P5: "P5 precision", P6: "P6 precision",
  };
  function attrLabel(hit, name) {
    var v = hit.attributes && hit.attributes[name];
    if (!v) return "Standard";
    return ATTR_LABELS[v] || String(v).replace(/_/g, " ");
  }
  function tier(h) { return IFX.tierLabel(h.xrefType); }
  var TIER_ORDER = { family: 0, family_variant: 1, verified: 2, iso_equivalent: 3, evidence: 4, evidence_unverified: 5 };

  var FACETS = [
    { key: "brand", label: "Brand", get: function (h) { return IFX.brandName(h.brand) || "Other"; } },
    { key: "tier", label: "Match", get: tier },
    { key: "seal", label: "Seal or shield", get: function (h) { return attrLabel(h, "seal"); } },
    { key: "clearance", label: "Clearance", get: function (h) { return attrLabel(h, "clearance"); } },
    { key: "cage", label: "Cage", get: function (h) { return attrLabel(h, "cage"); } },
    { key: "type", label: "Bearing type", get: function (h) { return h.bearingType ? IFX.typeName(h.bearingType) : "Other"; } },
  ];

  /* ── fetch ──────────────────────────────────────────────────────────── */
  function dimsLabel(p) {
    var f = function (v) { return v == null ? "any" : IFX.fmt.mm(v, app.unit); };
    return f(p.bore) + " × " + f(p.od) + " × " + f(p.width);
  }

  function runPn(q, opts) {
    opts = opts || {};
    var params = { q: q, limit: 60 };
    if (opts.brand) params.brand = opts.brand;
    IFX.state.write({ tab: "pn", q: q, brand: opts.brand || null, view: app.density === "table" ? "table" : null });
    return execute("pn", params, q);
  }
  function runDims(p) {
    var params = { limit: 60 };
    if (p.bore != null) params.bore = p.bore;
    if (p.od != null) params.od = p.od;
    if (p.width != null) params.width = p.width;
    if (p.tol && Number(p.tol) !== DEFAULT_TOL) params.tol = p.tol;
    IFX.state.write({ tab: "dims", bore: p.bore, od: p.od, width: p.width,
      tol: p.tol && Number(p.tol) !== DEFAULT_TOL ? p.tol : null, unit: app.unit === "in" ? "in" : null,
      view: app.density === "table" ? "table" : null });
    return execute("dims", params, dimsLabel(p), p);
  }
  function runFamily(fk) {
    IFX.state.write({ tab: "pn", family: fk });
    return execute("family", { family: fk, limit: 120 }, fk + " family");
  }

  function execute(mode, params, label, rawParams) {
    app.status('<span class="ifx-spinner" aria-hidden="true"></span>Searching…');
    app.skeletons("cards");
    app.head(false);
    return IFX.api.search(params).then(function (data) {
      var hits = (data.primary ? [data.primary] : []).concat(data.equivalents || []);
      last = { mode: mode, params: rawParams || params, data: data, hits: hits, label: label, familyKey: data.familyKey || null };
      facets = {};
      sortKey = "relevance";
      render();
      IFX.state.recent.add(mode === "dims" ? label : (params.q || params.family), mode === "dims" ? "dims" : "pn");
      IFX.track("ifx_search", { mode: mode, query: label, result_count: hits.length, had_apollo_match: hits.some(function (h) { return !!h.apolloMatch; }),
        intent: data.parsed ? data.parsed.intent : mode, family: data.familyKey || "" });
      return data;
    }).catch(function (err) {
      if (IFX.api.isAbort(err)) return null;
      console.error("[interchange] search error", err);
      app.error("Search unavailable", err.userMessage || "We couldn't reach the search service. Check your connection and try again.",
        function () { execute(mode, params, label, rawParams); });
      return null;
    });
  }

  /* ── render ─────────────────────────────────────────────────────────── */
  function render() {
    if (!last) return;
    var results = app.refs.results;
    var data = last.data, hits = last.hits, mode = last.mode;
    results.innerHTML = "";

    if (hits.length === 0) {
      app.head(false);
      if (data.oemMatch) results.appendChild(oemBanner(data.oemMatch));
      results.appendChild(emptyState());
      app.status("Nothing on file for " + esc(last.label) + ".");
      return;
    }

    app.head(true);
    renderToolbar();
    if (data.oemMatch) results.appendChild(oemBanner(data.oemMatch));
    if (data.fallback === "suffix_relaxed") results.appendChild(relaxedBanner(data));
    if (data.parsed && data.parsed.aliasResolved) results.appendChild(aliasResolvedBanner(data.parsed.aliasResolved, last.label));
    else if (data.parsed && data.parsed.aliasesTried && data.parsed.aliasesTried.length && hits.length) results.appendChild(aliasBanner(data.parsed));
    if (data.brandQuality === "hidden") results.appendChild(noteBanner("Only reference-grade listings", "These come from OEM parts lists or unnamed catalogs rather than a bearing manufacturer. Treat them as leads and confirm dimensions before ordering."));

    var primary = data.primary || null;
    var list = primary ? hits.slice(1) : hits.slice();
    var filtered = applyFiltersAndSort(list);
    var shown = filtered.length + (primary ? 1 : 0);

    if (app.density === "table" && mode !== "dims") {
      results.appendChild(buildTable(primary ? [primary].concat(filtered) : filtered, primary));
    } else {
      var grid = document.createElement("div");
      grid.className = "ifx-grid";
      if (primary) grid.appendChild(buildCard(primary, true, 0));
      if (mode === "dims") renderDimsGroups(grid, filtered);
      else filtered.forEach(function (h, i) { grid.appendChild(buildCard(h, false, i + 1)); });
      results.appendChild(grid);
      app.reveal(grid);
    }

    var n = hits.length;
    var msg = "<b>" + n + "</b> " + (n === 1 ? "result" : "results") + " for <span class=\"ifx-pn\">" + esc(last.label) + "</span>";
    if (filtersActive()) msg += " · showing " + shown + " of " + n;
    if (data.hiddenCount) msg += ' <span class="ifx-status__note">' + data.hiddenCount + " reference-grade listing" + (data.hiddenCount === 1 ? "" : "s") + " hidden</span>";
    app.status(msg);
    syncCompareTray();
  }

  function rerender() { if (last) render(); }

  /* ── cards ──────────────────────────────────────────────────────────── */
  var SUSPECT_NOTE = "Stored dimensions disagree with the family. Showing the family value; confirm before ordering.";

  function brandHtml(h) {
    var rank = typeof h.brandRank === "number" ? h.brandRank : 999;
    var cls = rank <= 10 ? "is-major" : rank <= 40 ? "is-known" : "";
    var q = h.brandQuality || "";
    var title = q === "legacy" ? "Legacy brand" : q === "reference" ? "Reference listing" : q === "oem" ? "OEM parts list" : q === "junk" ? "Unverified source" : "";
    return '<span class="ifx-brand ' + cls + '"' + (title ? ' title="' + esc(title) + '"' : "") + '><i class="ifx-brand__dot" aria-hidden="true"></i>' + esc(IFX.brandName(h.brand)) + "</span>";
  }

  function badgeHtml(h) {
    var t = h.xrefType || "family";
    if (last && last.mode === "dims") return "";
    var cls = { family: "family", family_variant: "variant", verified: "verified", iso_equivalent: "iso", evidence: "evidence", evidence_unverified: "unverified" }[t] || "family";
    var conf = typeof h.confidence === "number" && h.confidence > 0 && h.confidence <= 1 ? Math.round(h.confidence * 100) + "%" : "";
    var it = h.interchangeType && h.interchangeType !== t && h.interchangeType !== "exact" ? h.interchangeType.replace(/_/g, " ") : "";
    var tip = { family: "Same designation family from another brand.", variant: "Same family, a different design variant (type modifier).",
      verified: "Cross-reference confirmed by manufacturer catalog evidence with matching dimensions.",
      iso: "Same ISO boundary dimensions and series, derived from the designation system. Confirm suffixes and ratings.",
      evidence: "Published cross-reference, dimensions agree.", unverified: "Published cross-reference; one side lacks dimensions." }[cls];
    return '<span class="ifx-badge ifx-badge--' + cls + '" title="' + esc(tip) + '">' + esc(tier(h)) + (it ? " · " + esc(it) : "") + (conf ? ' <b>' + conf + "</b>" : "") + "</span>";
  }

  function specsHtml(h) {
    if (h.boreMm == null && h.odMm == null && h.widthMm == null) return "";
    var s = h.dimsSuspect && h.familyDims ? h.familyDims : h;
    var cell = function (label, mm) {
      return '<div class="ifx-spec"><dt>' + label + "</dt><dd data-mm=\"" + (mm == null ? "" : mm) + '\">' + esc(IFX.fmt.mm(mm, app.unit)) + "</dd></div>";
    };
    var mark = h.dimsSuspect ? '<button type="button" class="ifx-spec__flag" data-action="why-suspect" aria-label="Why these dimensions are flagged" title="' + esc(SUSPECT_NOTE) + '">†</button>' : "";
    return '<div class="ifx-specs-wrap"><dl class="ifx-specs' + (h.dimsSuspect ? " is-suspect" : "") + '">' + cell("d", s.boreMm) + cell("D", s.odMm) + cell("B", s.widthMm) + "</dl>" + mark + "</div>";
  }

  function deviationHtml(h) {
    if (!last || last.mode !== "dims") return "";
    var tol = Number(last.params.tol) || DEFAULT_TOL;
    if (tol <= DEFAULT_TOL) return "";
    var echo = last.data.queryEcho || {};
    var parts = [];
    var push = function (name, echoVal, hitVal) {
      var e = Number(echoVal);
      if (!Number.isFinite(e) || hitVal == null) return;
      var delta = Math.round((Number(hitVal) - e) * 100) / 100;
      if (Math.abs(delta) < 0.05) return;
      parts.push(name + " " + (delta > 0 ? "+" : "−") + Math.abs(delta) + " mm");
    };
    push("d", echo.bore, h.boreMm); push("D", echo.od, h.odMm); push("B", echo.width, h.widthMm);
    return parts.length ? '<div class="ifx-card__note">Off by ' + esc(parts.join(", ")) + "</div>" : "";
  }

  function buildCard(h, isPrimary, index) {
    var card = document.createElement("article");
    card.className = "ifx-card" + (isPrimary ? " ifx-card--primary" : "") + (h.dimsSuspect ? " is-suspect" : "");
    card.tabIndex = 0;
    card.dataset.id = h.id;
    card.dataset.reveal = "";
    card.style.setProperty("--ifx-i", String(Math.min(index, 12)));
    var saved = IFX.state.saved.has(h.id);
    var inCompare = compare.has(h.id);
    card.innerHTML =
      '<header class="ifx-card__top">' + brandHtml(h) + badgeHtml(h)
      + '<label class="ifx-card__compare"><input type="checkbox" data-compare' + (inCompare ? " checked" : "") + ' aria-label="Add to compare"><span>Compare</span></label>'
      + "</header>"
      + '<div class="ifx-card__pn"><span class="ifx-pn">' + esc(h.partNumber || "") + "</span>"
      + '<button type="button" class="ifx-icon-btn" data-action="copy" aria-label="Copy part number"><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><rect x="5" y="5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button>'
      + '<button type="button" class="ifx-icon-btn ifx-icon-btn--star' + (saved ? " is-on" : "") + '" data-action="save" aria-pressed="' + (saved ? "true" : "false") + '" aria-label="Save part"><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg></button>'
      + "</div>"
      + specsHtml(h) + deviationHtml(h)
      + '<div class="ifx-card__meta">' + (h.bearingType ? '<span class="ifx-type">' + esc(IFX.typeName(h.bearingType)) + "</span>" : "")
      + (h.provenance === "iso_derived" ? '<span class="ifx-type ifx-type--muted" title="Designation derived from the ISO system, not scraped from a catalog">derived</span>' : "")
      + (isPrimary ? '<span class="ifx-type ifx-type--accent">Your search</span>' : "") + "</div>"
      + '<footer class="ifx-card__cta"><button type="button" class="ifx-btn ifx-btn--rfq" data-action="rfq">Request a quote</button>'
      + (last && last.mode === "dims" ? '<button type="button" class="ifx-btn ifx-btn--ghost ifx-btn--sm" data-action="xref">Interchanges</button>'
        : '<button type="button" class="ifx-btn ifx-btn--ghost ifx-btn--sm" data-action="family" title="Every variant of this family">Family</button>')
      + "</footer>";
    wireCard(card, h);
    return card;
  }

  function wireCard(card, h) {
    card.querySelector('[data-action="rfq"]').addEventListener("click", function () {
      IFX.track("ifx_cta_click", { kind: "rfq" });
      app.openRfq(h.partNumber, h.brand, IFX.fmt.dims(h, app.unit));
    });
    var fam = card.querySelector('[data-action="family"]');
    if (fam) fam.addEventListener("click", function () { app.goFamily(last && last.familyKey ? familyKeyFor(h) : familyKeyFor(h)); });
    var xref = card.querySelector('[data-action="xref"]');
    if (xref) xref.addEventListener("click", function () { IFX.track("ifx_cta_click", { kind: "xref_jump" }); app.goSearch(h.partNumber); });
    card.querySelector('[data-action="copy"]').addEventListener("click", function (e) { copyPn(e.currentTarget, h.partNumber); });
    card.querySelector('[data-action="save"]').addEventListener("click", function (e) { toggleSave(e.currentTarget, h); });
    var cb = card.querySelector("[data-compare]");
    cb.addEventListener("change", function () { setCompare(h, cb.checked, cb); });
    var flag = card.querySelector('[data-action="why-suspect"]');
    if (flag) flag.addEventListener("click", function () { IFX.showToast(SUSPECT_NOTE + " Stored: " + IFX.fmt.dims(h, app.unit) + ".", { duration: 7000 }); });
    card.addEventListener("keydown", function (e) {
      if (e.target !== card) return;
      if (e.key === "c" || e.key === "C") { cb.checked = !cb.checked; setCompare(h, cb.checked, cb); }
      if (e.key === "Enter") { card.querySelector('[data-action="rfq"]').focus(); }
    });
  }

  function familyKeyFor(h) {
    if (h.familyKey) return h.familyKey;
    if (h.xrefType === "family" || h.xrefType === "family_variant") return (last && last.familyKey) || h.partNumber;
    return h.partNumber;
  }

  function copyPn(btn, pn) {
    var done = function () {
      btn.classList.add("is-done");
      btn.setAttribute("aria-label", "Copied");
      IFX.showToast(pn + " copied", { duration: 1800 });
      IFX.track("ifx_copy_pn", { pn: pn });
      setTimeout(function () { btn.classList.remove("is-done"); btn.setAttribute("aria-label", "Copy part number"); }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(pn).then(done, function () { fallbackCopy(pn); done(); });
    else { fallbackCopy(pn); done(); }
  }
  function fallbackCopy(text) {
    try {
      var ta = document.createElement("textarea");
      ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    } catch (err) { /* noop */ }
  }
  function toggleSave(btn, h) {
    var on = IFX.state.saved.toggle(h);
    btn.classList.toggle("is-on", on);
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    IFX.showToast(on ? "Saved " + h.partNumber : "Removed " + h.partNumber, { duration: 1600 });
    IFX.track("ifx_saved_toggle", { on: on, pn: h.partNumber });
  }

  /* ── table density ───────────────────────────────────────────────────── */
  var tableSort = { key: null, dir: 1 };
  function buildTable(rows, primary) {
    var wrap = document.createElement("div");
    wrap.className = "ifx-table-wrap";
    var cols = [
      { key: "brand", label: "Brand", get: function (h) { return IFX.brandName(h.brand); } },
      { key: "partNumber", label: "Part number", get: function (h) { return h.partNumber; }, mono: true },
      { key: "boreMm", label: "d", num: true }, { key: "odMm", label: "D", num: true }, { key: "widthMm", label: "B", num: true },
      { key: "bearingType", label: "Type", get: function (h) { return IFX.typeName(h.bearingType); } },
      { key: "tier", label: "Match", get: tier, sort: function (h) { return TIER_ORDER[h.xrefType || "family"]; } },
    ];
    var sorted = rows.slice();
    if (tableSort.key) {
      var col = cols.find(function (c) { return c.key === tableSort.key; });
      sorted.sort(function (a, b) {
        if (a === primary) return -1; if (b === primary) return 1;
        var av = col.sort ? col.sort(a) : col.num ? Number(a[col.key]) : String(col.get ? col.get(a) : a[col.key] || "").toLowerCase();
        var bv = col.sort ? col.sort(b) : col.num ? Number(b[col.key]) : String(col.get ? col.get(b) : b[col.key] || "").toLowerCase();
        if (av == null || (typeof av === "number" && !Number.isFinite(av))) return 1;
        if (bv == null || (typeof bv === "number" && !Number.isFinite(bv))) return -1;
        return (av < bv ? -1 : av > bv ? 1 : 0) * tableSort.dir;
      });
    }
    var table = document.createElement("table");
    table.className = "ifx-table";
    table.innerHTML = "<thead><tr>" + '<th scope="col" class="ifx-table__cmp"><span class="ifx-sr">Compare</span></th>'
      + cols.map(function (c) {
          var active = tableSort.key === c.key;
          return '<th scope="col" ' + (c.num ? 'class="is-num" ' : "") + 'aria-sort="' + (active ? (tableSort.dir > 0 ? "ascending" : "descending") : "none") + '">'
            + '<button type="button" class="ifx-table__sort" data-sort-key="' + c.key + '">' + esc(c.label) + '<span class="ifx-table__arrow" aria-hidden="true"></span></button></th>';
        }).join("")
      + '<th scope="col"><span class="ifx-sr">Actions</span></th></tr></thead><tbody></tbody>';
    var tbody = table.querySelector("tbody");
    sorted.forEach(function (h) {
      var tr = document.createElement("tr");
      tr.className = (h === primary ? "is-primary" : "") + (h.dimsSuspect ? " is-suspect" : "");
      var d = h.dimsSuspect && h.familyDims ? h.familyDims : h;
      tr.innerHTML = '<td class="ifx-table__cmp"><input type="checkbox" data-compare aria-label="Add to compare"' + (compare.has(h.id) ? " checked" : "") + "></td>"
        + "<td>" + brandHtml(h) + "</td>"
        + '<td><span class="ifx-pn">' + esc(h.partNumber) + '</span><button type="button" class="ifx-icon-btn" data-action="copy" aria-label="Copy part number"><svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><rect x="5" y="5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button></td>'
        + '<td class="is-num">' + esc(IFX.fmt.mm(d.boreMm, app.unit)) + "</td><td class=\"is-num\">" + esc(IFX.fmt.mm(d.odMm, app.unit)) + "</td><td class=\"is-num\">" + esc(IFX.fmt.mm(d.widthMm, app.unit)) + (h.dimsSuspect ? ' <span class="ifx-spec__flag" title="' + esc(SUSPECT_NOTE) + '">†</span>' : "") + "</td>"
        + "<td>" + esc(IFX.typeName(h.bearingType)) + "</td>"
        + "<td>" + badgeHtml(h) + "</td>"
        + '<td class="ifx-table__actions"><button type="button" class="ifx-btn ifx-btn--rfq ifx-btn--sm" data-action="rfq">Quote</button></td>';
      tr.querySelector('[data-action="rfq"]').addEventListener("click", function () { IFX.track("ifx_cta_click", { kind: "rfq" }); app.openRfq(h.partNumber, h.brand, IFX.fmt.dims(h, app.unit)); });
      tr.querySelector('[data-action="copy"]').addEventListener("click", function (e) { copyPn(e.currentTarget, h.partNumber); });
      var cb = tr.querySelector("[data-compare]");
      cb.addEventListener("change", function () { setCompare(h, cb.checked, cb); });
      tbody.appendChild(tr);
    });
    table.querySelectorAll("[data-sort-key]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var k = btn.dataset.sortKey;
        if (tableSort.key === k) tableSort.dir = -tableSort.dir; else { tableSort.key = k; tableSort.dir = 1; }
        render();
      });
    });
    wrap.appendChild(table);
    return wrap;
  }

  /* ── dims size groups ────────────────────────────────────────────────── */
  function renderDimsGroups(grid, list) {
    var groups = new Map();
    list.forEach(function (h, i) {
      var key = [h.boreMm, h.odMm, h.widthMm].join("|");
      var g = groups.get(key);
      if (!g) { g = { boreMm: h.boreMm, odMm: h.odMm, widthMm: h.widthMm, items: [], minDelta: Infinity, firstIdx: i }; groups.set(key, g); }
      g.items.push(h);
      if (typeof h.dimDeltaMm === "number" && h.dimDeltaMm < g.minDelta) g.minDelta = h.dimDeltaMm;
    });
    var ordered = Array.from(groups.values()).sort(function (a, b) { return (a.minDelta - b.minDelta) || (a.firstIdx - b.firstIdx); });
    var idx = 1;
    ordered.forEach(function (g) {
      var brands = new Set(g.items.map(function (h) { return (h.brand || "").toLowerCase(); }));
      var header = document.createElement("div");
      header.className = "ifx-sizegroup";
      header.innerHTML = '<span class="ifx-sizegroup__dims">' + esc(IFX.fmt.mm(g.boreMm, app.unit) + " × " + IFX.fmt.mm(g.odMm, app.unit) + " × " + IFX.fmt.mm(g.widthMm, app.unit)) + "</span>"
        + '<span class="ifx-sizegroup__count">' + brands.size + " brand" + (brands.size === 1 ? "" : "s") + " · " + g.items.length + " listing" + (g.items.length === 1 ? "" : "s") + "</span>";
      grid.appendChild(header);
      g.items.forEach(function (h) { grid.appendChild(buildCard(h, false, idx++)); });
    });
  }

  /* ── banners + empty states ──────────────────────────────────────────── */
  function banner(cls, title, bodyHtml) {
    var el = document.createElement("div");
    el.className = "ifx-banner ifx-banner--" + cls;
    el.innerHTML = '<div class="ifx-banner__title">' + esc(title) + '</div><div class="ifx-banner__body">' + bodyHtml + "</div>";
    return el;
  }
  function noteBanner(title, text) { return banner("note", title, esc(text)); }
  function oemBanner(om) {
    var machines = (om.machines || []).slice(0, 6).map(function (m) {
      return "<li><b>" + esc(m.manufacturer) + " " + esc(m.model) + "</b>" + (m.location ? " · " + esc(m.location) : "") + "</li>";
    }).join("");
    var more = (om.machines || []).length > 6 ? "<li>+ " + (om.machines.length - 6) + " more</li>" : "";
    return banner("oem", "OEM part number matched",
      "That number is bearing <b>" + esc(IFX.brandName(om.bearingBrand)) + ' <span class="ifx-pn">' + esc(om.bearingPn || "") + "</span></b>"
      + (om.bearingType ? " (" + esc(IFX.typeName(om.bearingType)) + ")" : "") + ". Its cross-brand equivalents are below.<ul>" + machines + more + "</ul>");
  }
  function relaxedBanner(data) {
    var attrs = data.relaxedAttributes || {};
    var list = Object.keys(attrs).map(function (k) { return ATTR_LABELS[attrs[k]] || String(attrs[k]).replace(/_/g, " "); }).join(", ");
    return banner("relaxed", "No exact variant on file",
      "Nothing is listed with " + (list ? "<b>" + esc(list) + "</b>" : "that suffix") + ", so these are the <b>" + esc(data.relaxedBase || "") + "</b> family. Suffix variants share the family interchange; confirm the seal, clearance or cage when you order.");
  }
  function aliasResolvedBanner(alias, query) {
    var kind = alias.kind === "dodge_part_number" ? "a Dodge part number" : alias.kind === "timken_set" ? "a Timken set number"
      : alias.kind === "old_designation" ? "an older designation" : "a recorded alias";
    return banner("note", "Part number resolved",
      '<span class="ifx-pn">' + esc(query) + "</span> is " + kind + " for " + (alias.brand ? "<b>" + esc(IFX.brandName(alias.brand)) + "</b> " : "")
      + '<b><span class="ifx-pn">' + esc(alias.canonicalPn) + "</span></b>. Results below are for that designation.");
  }

  function aliasBanner(parsed) {
    return banner("note", "Searched the older designation", "The current designation had no listing, so these results are for <b>" + esc(parsed.aliasesTried.join(", ")) + "</b>.");
  }

  function emptyState() {
    var box = document.createElement("div");
    box.className = "ifx-empty";
    var q = last.mode === "dims" ? null : (last.params.q || last.params.family || "");
    var fk = last.familyKey || q;
    var fd = last.data.familyDims;
    var isMounted = /^(UC|UK|UEL|SA|SB|HC|NA|UCP|UCF|UCFL|UCT|UCPA)/i.test(String(fk || ""));
    var actions = [];
    if (last.mode !== "dims") {
      if (fd) actions.push('<button type="button" class="ifx-btn ifx-btn--ghost" data-action="dims-instead">Search by size ' + esc(IFX.fmt.mm(fd.boreMm, app.unit) + " × " + IFX.fmt.mm(fd.odMm, app.unit) + (fd.widthMm != null ? " × " + IFX.fmt.mm(fd.widthMm, app.unit) : "")) + "</button>");
      actions.push('<button type="button" class="ifx-btn ifx-btn--ghost" data-action="decode-instead">Decode this number</button>');
      if (isMounted) actions.push('<button type="button" class="ifx-btn ifx-btn--ghost" data-action="insert-instead">Search the insert ' + esc(String(fk).replace(/^(UC)(P|F|FL|T|PA|FB|FC|FA|HA|PH)/i, "$1")) + "</button>");
    } else {
      actions.push('<button type="button" class="ifx-btn ifx-btn--ghost" data-action="widen">Widen the tolerance</button>');
    }
    actions.push('<button type="button" class="ifx-btn ifx-btn--rfq" data-action="rfq">Request a quote</button>');
    box.innerHTML = '<div class="ifx-empty__title">Nothing on file for <span class="ifx-pn">' + esc(last.label) + "</span></div>"
      + "<p>" + (last.mode === "dims" ? "No listing carries exactly those dimensions. Try a looser tolerance, leave a field blank, or ask us."
        : isMounted ? "Mounted units are indexed by insert and housing. Try the insert designation, search by size, or ask us; our team looks up what the index does not have yet."
        : "It may be a designation we have not indexed yet, a typo, or an OEM number. Try one of these, or ask us and our team looks it up directly.") + "</p>"
      + '<div class="ifx-empty__actions">' + actions.join("") + "</div>"
      + '<div class="ifx-empty__nearest" data-nearest hidden></div>';
    var rfq = box.querySelector('[data-action="rfq"]');
    rfq.addEventListener("click", function () { IFX.track("ifx_cta_click", { kind: "rfq" }); app.openRfq(last.mode === "pn" ? q : "", "", last.mode === "dims" ? last.label : ""); });
    var dimsBtn = box.querySelector('[data-action="dims-instead"]');
    if (dimsBtn) dimsBtn.addEventListener("click", function () { app.goDims({ bore: fd.boreMm, od: fd.odMm, width: fd.widthMm, tol: 0.5 }); });
    var dec = box.querySelector('[data-action="decode-instead"]');
    if (dec) dec.addEventListener("click", function () { app.goDecode(q); });
    var ins = box.querySelector('[data-action="insert-instead"]');
    if (ins) ins.addEventListener("click", function () { app.goSearch(String(fk).replace(/^(UC)(P|F|FL|T|PA|FB|FC|FA|HA|PH)/i, "$1")); });
    var widen = box.querySelector('[data-action="widen"]');
    if (widen) widen.addEventListener("click", function () { app.goDims(Object.assign({}, last.params, { tol: Number(last.params.tol) >= 1 ? 2 : 1 })); });
    if (last.mode !== "dims" && q) loadNearest(box.querySelector("[data-nearest]"), q);
    return box;
  }

  function loadNearest(el, q) {
    IFX.api.family(q, 10).then(function () { /* found after all — nothing to add */ }).catch(function (err) {
      var nearest = err && err.data && err.data.nearest;
      if (!nearest || !nearest.length || !el) return;
      el.hidden = false;
      el.innerHTML = '<span class="ifx-popular__label">Nearby families</span><div class="ifx-popular__chips">'
        + nearest.map(function (n) { return '<button type="button" class="ifx-chip" data-q="' + esc(n) + '">' + esc(n) + "</button>"; }).join("") + "</div>";
      el.querySelectorAll("[data-q]").forEach(function (b) { b.addEventListener("click", function () { app.goSearch(b.dataset.q); }); });
    });
  }

  /* ── filters + sort ──────────────────────────────────────────────────── */
  function filtersActive() { return Object.keys(facets).some(function (k) { return facets[k] && facets[k].size > 0; }); }
  function matches(h) {
    for (var i = 0; i < FACETS.length; i++) {
      var f = FACETS[i], set = facets[f.key];
      if (set && set.size > 0 && !set.has(f.get(h))) return false;
    }
    return true;
  }
  function dimDelta(h) { return typeof h.dimDeltaMm === "number" ? h.dimDeltaMm : Infinity; }
  function applyFiltersAndSort(list) {
    var out = list.filter(matches);
    if (sortKey === "closest") out.sort(function (a, b) { return dimDelta(a) - dimDelta(b); });
    else if (sortKey === "brand") out.sort(function (a, b) {
      var ab = IFX.brandName(a.brand).toLowerCase(), bb = IFX.brandName(b.brand).toLowerCase();
      if (ab !== bb) return ab < bb ? -1 : 1;
      return String(a.partNumber || "").localeCompare(String(b.partNumber || ""));
    });
    else if (sortKey === "tier") out.sort(function (a, b) { return (TIER_ORDER[a.xrefType || "family"] - TIER_ORDER[b.xrefType || "family"]) || ((a.brandRank || 999) - (b.brandRank || 999)); });
    return out;
  }

  function facetGroupsHtml(hits) {
    var html = "";
    FACETS.forEach(function (f) {
      var counts = new Map();
      hits.forEach(function (h) { var v = f.get(h); counts.set(v, (counts.get(v) || 0) + 1); });
      if (counts.size < 2) return;
      var buckets = Array.from(counts.entries()).sort(function (a, b) { return (b[1] - a[1]) || (a[0] < b[0] ? -1 : 1); });
      html += '<div class="ifx-facet"><span class="ifx-facet__label">' + esc(f.label) + '</span><div class="ifx-facet__chips">'
        + buckets.map(function (e) {
            var pressed = !!(facets[f.key] && facets[f.key].has(e[0]));
            return '<button type="button" class="ifx-chip ifx-chip--facet" aria-pressed="' + pressed + '" data-facet="' + esc(f.key) + '" data-value="' + esc(e[0]) + '">' + esc(e[0]) + ' <span class="ifx-chip__count">' + e[1] + "</span></button>";
          }).join("")
        + "</div></div>";
    });
    return html;
  }

  function renderToolbar() {
    var hits = last.hits;
    var html = facetGroupsHtml(hits);
    var hasFacets = html !== "";
    refs.filters.innerHTML = html;
    refs.filters.hidden = !hasFacets || refs.filters.dataset.collapsed === "1";
    refs.filtersSheet.innerHTML = html || "<p class=\"ifx-panel__hint\">Nothing to filter for this search.</p>";
    [refs.filters, refs.filtersSheet].forEach(function (host) {
      host.querySelectorAll("[data-facet]").forEach(function (chip) {
        chip.addEventListener("click", function () { toggleFacet(chip.dataset.facet, chip.dataset.value, host === refs.filtersSheet); });
      });
    });
    var n = Object.keys(facets).reduce(function (s, k) { return s + (facets[k] ? facets[k].size : 0); }, 0);
    refs.filterCount.textContent = String(n);
    refs.filterCount.hidden = n === 0;
    refs.filtersBtn.hidden = !hasFacets;
    refs.filtersBtn.setAttribute("aria-pressed", refs.filters.hidden ? "false" : "true");

    var opts = [["relevance", "Best match"], ["tier", "Match quality"], ["brand", "Brand A–Z"]];
    if (last.mode === "dims") opts.splice(1, 0, ["closest", "Closest size"]);
    if (!opts.some(function (o) { return o[0] === sortKey; })) sortKey = "relevance";
    refs.sort.innerHTML = opts.map(function (o) { return '<option value="' + o[0] + '"' + (o[0] === sortKey ? " selected" : "") + ">Sort: " + esc(o[1]) + "</option>"; }).join("");
    refs.densityToggle.hidden = last.mode === "dims";
  }

  function toggleFacet(key, value, fromSheet) {
    if (!facets[key]) facets[key] = new Set();
    if (facets[key].has(value)) facets[key].delete(value); else facets[key].add(value);
    if (facets[key].size === 0) delete facets[key];
    IFX.track("ifx_filter_change", { facet: key });
    render();
    var host = fromSheet ? refs.filtersSheet : refs.filters;
    var chip = host.querySelector('[data-facet="' + key + '"][data-value="' + value.replace(/"/g, '\\"') + '"]');
    if (chip) chip.focus();
  }
  function clearFilters() { facets = {}; IFX.track("ifx_filter_change", { facet: "clear" }); render(); }

  /* ── compare ─────────────────────────────────────────────────────────── */
  function setCompare(h, on, cb) {
    if (on) {
      if (compare.size >= COMPARE_MAX && !compare.has(h.id)) {
        if (cb) cb.checked = false;
        IFX.showToast("Compare holds up to " + COMPARE_MAX + " bearings. Remove one first.", { tone: "err" });
        return;
      }
      compare.set(h.id, h);
    } else compare.delete(h.id);
    app.root.querySelectorAll("[data-compare]").forEach(function (box) {
      var card = box.closest("[data-id]") || box.closest("tr");
      var id = card && card.dataset ? card.dataset.id : null;
      if (id) box.checked = compare.has(id);
    });
    syncCompareTray();
  }
  function syncCompareTray() {
    var ids = Array.from(compare.keys());
    refs.tray.hidden = ids.length === 0;
    refs.trayItems.innerHTML = ids.map(function (id) {
      var h = compare.get(id);
      return '<button type="button" class="ifx-chip ifx-chip--tray" data-remove="' + esc(id) + '" aria-label="Remove ' + esc(h.partNumber) + ' from compare">'
        + '<span class="ifx-brand">' + esc(IFX.brandName(h.brand)) + '</span> <span class="ifx-pn">' + esc(h.partNumber) + '</span><span class="ifx-chip__x" aria-hidden="true">×</span></button>';
    }).join("") + (ids.length ? '<span class="ifx-tray__hint">' + ids.length + " of " + COMPARE_MAX + "</span>" : "");
    refs.trayItems.querySelectorAll("[data-remove]").forEach(function (b) {
      b.addEventListener("click", function () { var h = compare.get(b.dataset.remove); if (h) setCompare(h, false); });
    });
    refs.compareOpen.disabled = ids.length < 2;
    refs.compareToggle.setAttribute("aria-pressed", ids.length ? "true" : "false");
    IFX.state.write({ cmp: ids.length ? ids.join(",") : null }, { merge: true, replace: true });
  }
  function clearCompare() { compare.clear(); app.root.querySelectorAll("[data-compare]").forEach(function (b) { b.checked = false; }); syncCompareTray(); }

  function openCompare() {
    var ids = Array.from(compare.keys());
    if (ids.length < 2) return;
    var dlg = refs.compareDialog, body = refs.compareBody;
    body.innerHTML = '<div class="ifx-skeleton-block" aria-hidden="true"></div>';
    if (typeof dlg.showModal === "function") { if (!dlg.open) dlg.showModal(); } else dlg.setAttribute("open", "");
    IFX.track("ifx_compare_open", { count: ids.length });
    IFX.api.compare(ids).then(function (data) { body.innerHTML = compareTableHtml(data); wireCompareBody(body, data); })
      .catch(function (err) { if (!IFX.api.isAbort(err)) body.innerHTML = '<div class="ifx-error"><div class="ifx-error__title">Comparison unavailable</div><p>' + esc(err.userMessage || "Try again in a moment.") + "</p></div>"; });
  }
  function compareTableHtml(data) {
    var items = data.items || [];
    var fields = data.fields || [];
    var fmtVal = function (item, f) {
      var v = item[f.key];
      if (v == null) return '<span class="ifx-muted">—</span>';
      if (f.unit === "mm") return esc(IFX.fmt.mm(v, app.unit));
      if (f.unit === "N") return esc(IFX.fmt.num(v)) + " N";
      if (f.unit === "rpm") return esc(IFX.fmt.num(v)) + " rpm";
      if (f.unit === "kg") return esc(v) + " kg";
      return esc(String(v));
    };
    var head = "<tr><th scope=\"row\"></th>" + items.map(function (it) {
      return '<th scope="col"><span class="ifx-brand">' + esc(IFX.brandName(it.brand)) + '</span><span class="ifx-pn">' + esc(it.partNumber) + "</span>" + (it.bearingType ? '<span class="ifx-type">' + esc(IFX.typeName(it.bearingType)) + "</span>" : "") + "</th>";
    }).join("") + "</tr>";
    var rows = fields.filter(function (f) { return items.some(function (it) { return it[f.key] != null; }); }).map(function (f) {
      var vals = items.map(function (it) { return it[f.key]; });
      var nums = vals.filter(function (v) { return typeof v === "number"; });
      var differ = nums.length > 1 && Math.max.apply(null, nums) !== Math.min.apply(null, nums);
      return '<tr class="' + (differ ? "is-diff" : "") + '"><th scope="row">' + esc(f.label) + "</th>" + items.map(function (it) { return "<td>" + fmtVal(it, f) + "</td>"; }).join("") + "</tr>";
    }).join("");
    var attrRows = [["seal", "Seal / shield"], ["clearance", "Clearance"], ["cage", "Cage"], ["precision", "Precision"]].map(function (pair) {
      var vals = items.map(function (it) { var a = it.attributes || {}; return a[pair[0]] ? (ATTR_LABELS[a[pair[0]]] || a[pair[0]]) : "Standard"; });
      if (vals.every(function (v) { return v === "Standard"; })) return "";
      var differ = new Set(vals).size > 1;
      return '<tr class="' + (differ ? "is-diff" : "") + '"><th scope="row">' + pair[1] + "</th>" + vals.map(function (v) { return "<td>" + esc(v) + "</td>"; }).join("") + "</tr>";
    }).join("");
    var prov = "<tr><th scope=\"row\">Source</th>" + items.map(function (it) {
      var p = it.provenance === "iso_derived" ? "Derived from ISO designation" : it.canonical ? "Verified catalog record" : "Catalog candidate";
      return "<td>" + esc(p) + (it.dimsSource ? '<span class="ifx-muted"> · dims: ' + esc(String(it.dimsSource).replace(/_/g, " ")) + "</span>" : "") + "</td>";
    }).join("") + "</tr>";
    var cta = "<tr><th scope=\"row\"></th>" + items.map(function (it) { return '<td><button type="button" class="ifx-btn ifx-btn--rfq ifx-btn--sm" data-rfq-pn="' + esc(it.partNumber) + '" data-rfq-brand="' + esc(it.brand) + '">Request a quote</button></td>'; }).join("") + "</tr>";
    return '<div class="ifx-table-wrap"><table class="ifx-table ifx-table--compare"><thead>' + head + "</thead><tbody>" + rows + attrRows + prov + cta + "</tbody></table></div>"
      + '<p class="ifx-panel__hint">Rows with differing values are highlighted. Load ratings and speeds are shown when the catalog record carries them.</p>';
  }
  function wireCompareBody(body) {
    body.querySelectorAll("[data-rfq-pn]").forEach(function (b) {
      b.addEventListener("click", function () { app.openRfq(b.dataset.rfqPn, b.dataset.rfqBrand); });
    });
  }

  /* ── init ────────────────────────────────────────────────────────────── */
  function init(application) {
    app = application;
    refs = {
      filters: app.root.querySelector("[data-filters]"),
      filtersSheet: app.root.querySelector("[data-filters-sheet]"),
      filtersBtn: app.root.querySelector('[data-action="open-filters"]'),
      filterCount: app.root.querySelector("[data-filter-count]"),
      sort: app.root.querySelector("[data-sort]"),
      densityToggle: app.root.querySelector("[data-density-toggle]"),
      tray: app.root.querySelector("[data-compare-tray]"),
      trayItems: app.root.querySelector("[data-compare-items]"),
      compareOpen: app.root.querySelector('[data-action="open-compare"]'),
      compareToggle: app.root.querySelector('[data-action="toggle-compare"]'),
      compareDialog: app.root.querySelector('.ifx-dialog[data-modal="compare"]'),
      compareBody: app.root.querySelector("[data-compare-body]"),
      filtersDialog: app.root.querySelector('.ifx-dialog[data-modal="filters"]'),
    };
    refs.sort.addEventListener("change", function () { sortKey = refs.sort.value; IFX.track("ifx_filter_change", { facet: "sort" }); render(); });
    refs.filtersBtn.addEventListener("click", function () {
      var mobile = window.matchMedia("(max-width: 767px)").matches;
      if (mobile) {
        var dlg = refs.filtersDialog;
        if (typeof dlg.showModal === "function") dlg.showModal(); else dlg.setAttribute("open", "");
      } else {
        var collapsed = refs.filters.dataset.collapsed === "1";
        refs.filters.dataset.collapsed = collapsed ? "0" : "1";
        refs.filters.hidden = !collapsed;
        refs.filtersBtn.setAttribute("aria-pressed", collapsed ? "true" : "false");
      }
    });
    app.root.querySelector('[data-action="clear-filters"]').addEventListener("click", clearFilters);
    refs.compareOpen.addEventListener("click", openCompare);
    refs.compareToggle.addEventListener("click", function () {
      if (compare.size >= 2) openCompare();
      else IFX.showToast("Tick Compare on two to four results, then open the comparison.", { duration: 3500 });
    });
    app.root.querySelector('[data-action="clear-compare"]').addEventListener("click", clearCompare);
    [refs.compareDialog, refs.filtersDialog].forEach(function (dlg) {
      dlg.querySelectorAll("[data-close]").forEach(function (b) { b.addEventListener("click", function () { if (dlg.open) dlg.close(); else dlg.removeAttribute("open"); }); });
      dlg.addEventListener("click", function (e) { if (e.target === dlg) { if (dlg.open) dlg.close(); } });
    });
    // Restore a ?cmp= selection lazily: ids are resolved once results render.
    var st = IFX.state.read();
    if (st.cmp) {
      var wanted = new Set(st.cmp.split(","));
      var once = function () {
        if (!last) return;
        last.hits.forEach(function (h) { if (wanted.has(h.id)) compare.set(h.id, h); });
        document.removeEventListener("ifx_search", once);
        if (compare.size) render();
      };
      document.addEventListener("ifx_search", once);
    }
  }

  var view = { init: init, runPn: runPn, runDims: runDims, runFamily: runFamily, rerender: rerender, hasResults: function () { return !!last; }, last: function () { return last; } };
  IFX.views.search = view;
  IFX.define("view:search", view);
})();
